import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { AlertCircle, RefreshCw, SearchX, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useOrganization } from "@/hooks/use-organization";

export function PageMessage({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <div className="mx-auto w-full max-w-xl px-3 py-10 sm:px-6">
      <div className="rounded-2xl border border-slate-200 bg-white px-6 py-12 text-center">
        <Icon
          aria-hidden="true"
          className="mx-auto mb-4 h-12 w-12 text-slate-300"
        />
        <h2 className="mb-1.5 text-base font-semibold text-slate-900">
          {title}
        </h2>
        <p className="text-sm text-slate-500">{description}</p>
        {children && (
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Shown when a page's record isn't in the selected business unit. The org and
 * BU switchers keep the current path, so switching while viewing a record
 * lands here with an ID from the previous business unit.
 */
export function RecordNotInBusinessUnit({
  title,
  recordLabel,
  back,
}: {
  title: string;
  recordLabel: string;
  back: { to: string; label: string };
}) {
  const { selectedOrgBusinessUnit } = useOrganization();
  return (
    <PageMessage
      icon={SearchX}
      title={title}
      description={`This ${recordLabel} isn't available in ${selectedOrgBusinessUnit.name}. It may belong to a different business unit, or you may no longer have access.`}
    >
      <Button asChild variant="outline">
        <Link to={back.to}>{back.label}</Link>
      </Button>
    </PageMessage>
  );
}

export function RecordLoadFailed({
  recordLabel,
  onRetry,
}: {
  recordLabel: string;
  onRetry: () => void;
}) {
  return (
    <PageMessage
      icon={AlertCircle}
      title={`Couldn't load this ${recordLabel}`}
      description="Something went wrong. Check your connection and try again."
    >
      <Button variant="outline" onClick={onRetry}>
        <RefreshCw aria-hidden="true" className="mr-2 h-4 w-4" />
        Retry
      </Button>
    </PageMessage>
  );
}
